import React, {useEffect, useRef} from 'react'
import {useTranslation} from 'react-i18next'
import styled from 'styled-components'

import {Text, H3} from '../../../Atoms/Typography'
import {IconDelete, IconEditNote} from '../../../../Assets/Icons/TinyIcons'
import {IconDocument, IconDocumentLinked, IconSettings} from '../../../../Assets/Icons/LargeIcons'
import {colors} from '../../../../sharedComponents/colors'
import {IconButton} from '../../../Atoms/Buttons'
import {AccordionItem} from '../../../Atoms/Accordion'
import {Document, DocumentType} from '../../../../state/documents/state'
import {A11yIconListItem, OffsetListItem} from '../../../Atoms/Lists'

const ListItem = AccordionItem

const NoItemContent = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  align-items: start;
  justify-items: start;
`

const DocumentActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`

interface DocumentItemProps {
  id: string
  type: string
  name: string
  onConfigure?: (id: string) => void
  onClick?: (id: string) => void
  isTarget?: boolean
  document: Document
  onDelete?: (document: Document) => void
  useSettingsIcon?: boolean
}

const DocumentListItem = ({
  id,
  type,
  name,
  onClick,
  onConfigure,
  isTarget,
  document,
  onDelete,
  useSettingsIcon
}: DocumentItemProps) => {
  const {t} = useTranslation()
  const ref = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (isTarget && ref.current) {
      setTimeout(() => {
        const el = ref.current
        if (el) {
          try {
            el.scrollIntoView({block: 'center'})
          } catch (e) {
            el.scrollIntoView()
          }
        }
      }, 0)
    }
  }, [isTarget])

  const handleConfigure = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (onConfigure) {
      e.preventDefault()
      e.stopPropagation()
      if (onConfigure) onConfigure(id)
    }
  }
  const handleClick = () => {
    if (onClick) {
      onClick(id)
    }
  }
  const handleDelete = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (onDelete) {
      e.preventDefault()
      e.stopPropagation()
      onDelete(document)
    }
  }

  return (
    <div ref={ref} style={isTarget ? {backgroundColor: colors.system.lightGrey_5} : undefined}>
      <A11yIconListItem
        offset={5.3}
        icon={type === DocumentType.Internal ? <IconDocument /> : <IconDocumentLinked />}
        onClick={handleClick}
        content={<H3>{name}</H3>}
        action={
          <DocumentActionRow>
            {onConfigure ? (
              <IconButton
                buttonProps={{
                  onClick: handleConfigure,
                  'aria-label': t('documents:aria.editFile', 'Edit {{name}}', {name})
                }}
                icon={
                  useSettingsIcon ? (
                    <IconSettings color={colors.brand.cobalt} />
                  ) : (
                    <IconEditNote color={colors.brand.cobalt} />
                  )
                }
              />
            ) : null}
            {onDelete ? (
              <IconButton
                buttonProps={{
                  onClick: handleDelete,
                  'aria-label': t('documents:aria.deleteFile', 'Delete {{name}}', {name})
                }}
                icon={<IconDelete color={colors.brand.cobalt} />}
              />
            ) : null}
          </DocumentActionRow>
        }
      />
    </div>
  )
}

export const NoDocumentsItem = () => {
  const {t} = useTranslation('documents')
  return (
    <ListItem>
      <NoItemContent>
        <OffsetListItem offset={3.6}>
          <Text level={1}>{t('documents:messages.noDocuments', 'No documents')}</Text>
        </OffsetListItem>
      </NoItemContent>
    </ListItem>
  )
}

export const NoDocumentsOrFoldersItem = () => {
  const {t} = useTranslation('documents')
  return (
    <ListItem>
      <NoItemContent>
        <Text level={1}>{t('documents:messages.noDocumentsOrFolder', 'No documents or folders.')}</Text>
      </NoItemContent>
    </ListItem>
  )
}

export default DocumentListItem
